import { logger } from "@/utils/logger";
import { storeDealerData, getDealerID, readDealerCookie } from './dealerDataUtils';
import { storeInterestData } from './interestDataUtils';
import { addNavigationHistoryEntry } from './navigationHistoryUtils';

export interface HTMLContextData {
  initialLanguage: 'fr' | 'en';
  dealerData: any;
  priceDisclaimer: string | null;
  vehicleData: any;
  shouldSendVDPContext?: boolean;
  vdpContextMessage?: string;
}

export interface WebsiteCurrentVehicle {
  vehicleCategory?: string;
  status?: string;
  make?: string;
  model?: string;
  year?: string;
  vin?: string;
  trim?: string;
  displayedPrice?: string;
  msrp?: string;
  fuelType?: string;
  transmission?: string;
  driveTrain?: string;
  engine?: string;
  extColor?: string;
  intColor?: string;
  sd_city_test?: string;
  traffic_type?: string;
  dealerBAC?: string;
  viewed_datestamp?: string;
}

// Detect and store VDP JSON data early on page load
// Helper function to recursively remove keys containing 'tooltip'
const filterTooltipFields = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => filterTooltipFields(item));
  }

  if (typeof obj === 'object') {
    const filtered: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key) && !key.toLowerCase().includes('tooltip')) {
        filtered[key] = filterTooltipFields(obj[key]);
      }
    }
    return filtered;
  }

  return obj;
};

export const detectAndStoreVDPJson = () => {
  try {
    const win = window as any;
    if (win.__vdpJSON) {
      logger.log('✅ window.__vdpJSON detected on page - storing FRESH data');
      const filteredData = filterTooltipFields(win.__vdpJSON);
      
      // Check if this is a Showroom VDP: has 'id' field but NOT 'sn' and 'vin' fields
      const hasId = filteredData && 'id' in filteredData;
      const hasSn = filteredData && 'sn' in filteredData;
      const hasVin = filteredData && 'vin' in filteredData;
      
      if (hasId && !hasSn && !hasVin) {
        // This is a Showroom VDP
        logger.log('✅ Showroom VDP detected (has id, no sn/vin) - storing as VDPShowroomjsonData');
        localStorage.setItem('VDPShowroomjsonData', JSON.stringify(filteredData));
        // Clear regular VDP data since this is a showroom page
        localStorage.removeItem('VDPjsonData');
      } else {
        // This is a regular VDP (inventory vehicle)
        localStorage.setItem('VDPjsonData', JSON.stringify(filteredData));
        logger.log('✅ VDP JSON data stored in localStorage (tooltip fields removed)');
        // Clear showroom data since this is a regular VDP
        localStorage.removeItem('VDPShowroomjsonData');
      }
    } else {
      logger.log('⚠️ No window.__vdpJSON found on page - CLEARING localStorage');
      // CRITICAL: Clear both VDP data types if not present on current page
      localStorage.removeItem('VDPjsonData');
      localStorage.removeItem('VDPShowroomjsonData');
    }
  } catch (vdpError) {
    console.error('❌ Error detecting/storing VDP JSON:', vdpError);
  }
};

export const readWebsiteCurrentVehicle = async (): Promise<WebsiteCurrentVehicle | null> => {
  try {
    logger.log('=== READING WEBSITE CURRENT VEHICLE DATA ===');
    
    // Check if any of the vehicle input fields exist on the page
    const vehicleCategoryInput = document.querySelector('input[name="vehicleCategory"]') as HTMLInputElement;
    const statusInput = document.querySelector('input[name="status"]') as HTMLInputElement;
    const makeInput = document.querySelector('input[name="make"]') as HTMLInputElement;
    const modelInput = document.querySelector('input[name="model"]') as HTMLInputElement;
    const yearInput = document.querySelector('input[name="year"]') as HTMLInputElement;
    const vinInput = document.querySelector('input[name="vin"]') as HTMLInputElement;
    const trimInput = document.querySelector('input[name="trim"]') as HTMLInputElement;
    const displayedPriceInput = document.querySelector('input[name="displayedPrice"]') as HTMLInputElement;
    const msrpInput = document.querySelector('input[name="msrp"]') as HTMLInputElement;
    const fuelTypeInput = document.querySelector('input[name="fuelType"]') as HTMLInputElement;
    const transmissionInput = document.querySelector('input[name="transmission"]') as HTMLInputElement;
    const driveTrainInput = document.querySelector('input[name="driveTrain"]') as HTMLInputElement;
    const engineInput = document.querySelector('input[name="engine"]') as HTMLInputElement;
    const extColorInput = document.querySelector('input[name="extColor"]') as HTMLInputElement;
    const intColorInput = document.querySelector('input[name="intColor"]') as HTMLInputElement;
    const sdCityTestInput = document.querySelector('input[name="sd_city_test"]') as HTMLInputElement;
    const trafficTypeInput = document.querySelector('input[name="traffic_type"]') as HTMLInputElement;
    const dealerBACInput = document.querySelector('input[name="dealerBAC"]') as HTMLInputElement;

    // Also check for vehicle specification spans
    const specsVin = document.getElementById('specsvin') as HTMLSpanElement;
    const specsKM = document.getElementById('specsKM') as HTMLSpanElement;
    const specsDoor = document.getElementById('specsDoor') as HTMLSpanElement;
    const specsSeat = document.getElementById('specsSeat') as HTMLSpanElement;
    const specsExtColor = document.getElementById('specsExtColor') as HTMLSpanElement;
    const specsIntColor = document.getElementById('specsIntColor') as HTMLSpanElement;
    const specsBodyType = document.getElementById('specsBodyType') as HTMLSpanElement;
    const specsVersion = document.getElementById('specsVersion') as HTMLSpanElement;
    const specsModel = document.getElementById('specsModel') as HTMLSpanElement;
    const specsDriveTrain = document.getElementById('specsDriveTrain') as HTMLSpanElement;
    const specsTransmission = document.getElementById('specsTransmission') as HTMLSpanElement;
    const specsFuel = document.getElementById('specsFuel') as HTMLSpanElement;
    const specsEngine = document.getElementById('specsEngine') as HTMLSpanElement;
    const specsCylinders = document.getElementById('specsCylinders') as HTMLSpanElement;

    // If any of the key vehicle fields exist, read all fields
    if (vehicleCategoryInput || makeInput || modelInput || yearInput || vinInput) {
      const websiteCurrentVehicle: WebsiteCurrentVehicle = {
        vehicleCategory: vehicleCategoryInput?.value || '',
        status: statusInput?.value || '',
        make: makeInput?.value || '',
        model: modelInput?.value || '',
        year: yearInput?.value || '',
        vin: vinInput?.value || '',
        trim: trimInput?.value || '',
        displayedPrice: displayedPriceInput?.value || '',
        msrp: msrpInput?.value || '',
        fuelType: fuelTypeInput?.value || '',
        transmission: transmissionInput?.value || '',
        driveTrain: driveTrainInput?.value || '',
        engine: engineInput?.value || '',
        extColor: extColorInput?.value || '',
        intColor: intColorInput?.value || '',
        sd_city_test: sdCityTestInput?.value || '',
        traffic_type: trafficTypeInput?.value || '',
        dealerBAC: dealerBACInput?.value || '',
        viewed_datestamp: new Date().toISOString()
      };

      // Store in localStorage
      localStorage.setItem('Website_Current_Vehicle', JSON.stringify(websiteCurrentVehicle));
      logger.log('✅ Website current vehicle data stored in localStorage:', websiteCurrentVehicle);
      
      return websiteCurrentVehicle;
    } else {
      logger.log('⚠️ No website current vehicle input fields found on the page');
      return null;
    }
  } catch (error) {
    console.error('Error reading website current vehicle data from HTML:', error);
    return null;
  }
};

// REMOVED: sendVehicleContextToOpenAI function - VDP context should only be sent when user sends a message

// Cache for context to avoid repeated parsing within same page view
let cachedContext: HTMLContextData | null = null;
let isInitialized = false;

// Reset cache to force fresh read on page navigation
export const resetContextCache = () => {
  cachedContext = null;
  isInitialized = false;
  logger.log('🔄 Context cache reset - will read fresh data on next call');
};

export const readContextFromHTML = (): HTMLContextData => {
  // CRITICAL: Always read FRESH data on every call - never use cache
  // This ensures VDP context is always current and never stale from previous pages
  cachedContext = null;
  isInitialized = false;
  
  // Mark as initialized to prevent concurrent calls
  isInitialized = true;

  let initialLanguage: 'fr' | 'en' = 'en';
  let dealerData = null;
  let priceDisclaimer = null;
  let vehicleData = null;
  let shouldSendVDPContext = false;
  let vdpContextMessage = '';

  // Read initial language from HTML page
  try {
    const languageInput = document.getElementById('textLanguage') as HTMLInputElement;
    if (languageInput) {
      const languageValue = languageInput.value;
      if (languageValue === 'FRENCH') {
        initialLanguage = 'fr';
      } else if (languageValue === 'ENGLISH') {
        initialLanguage = 'en';
      }
    } else {
      
      // Fallback: Check for content-language meta tag
      try {
        const metaTag = document.querySelector('meta[http-equiv="content-language"]') as HTMLMetaElement;
        if (metaTag && metaTag.content) {
          const contentLang = metaTag.content.toLowerCase();
          const langCode = contentLang.split('-')[0];
          if (langCode === 'fr') {
            initialLanguage = 'fr';
          } else if (langCode === 'en') {
            initialLanguage = 'en';
          }
        }
      } catch (metaError) {
        // Silent fail
      }
    }
  } catch (error) {
    console.error('Error reading initial language from HTML:', error);
  }

  // Read price disclaimer from HTML page
  try {
    const priceDisclaimerInput = document.getElementById('ADMIN_PRICE_DISCLAIMER_NEW_TEXT') as HTMLInputElement;
    if (priceDisclaimerInput && priceDisclaimerInput.value && priceDisclaimerInput.value.trim() !== '') {
      priceDisclaimer = priceDisclaimerInput.value.trim();
      localStorage.setItem('Price_Disclaimer', priceDisclaimer);
    }
  } catch (error) {
    console.error('Error reading price disclaimer from HTML:', error);
  }

  // Read pageType and store as Site_Section in localStorage
  let siteSection = '';
  try {
    const pageTypeInput = document.querySelector('input[name="pageType"]') as HTMLInputElement;
    if (pageTypeInput && pageTypeInput.value && pageTypeInput.value.trim() !== '') {
      siteSection = pageTypeInput.value.trim();
      localStorage.setItem('Site_Section', siteSection);
    }
  } catch (error) {
    console.error('Error reading pageType from HTML:', error);
  }

  // CRITICAL: VDP context is completely passive - only prepared for use when user sends a message
  // No automatic OpenAI calls should happen during page initialization

  // Read vehicle data from HTML page and store as interest data
  try {
    const brandInput = document.querySelector('input[name="brand"].brandemail') as HTMLInputElement;
    const modelInput = document.querySelector('input[name="model"].modelemail') as HTMLInputElement;
    const yearInput = document.querySelector('input[name="year"].yearemail') as HTMLInputElement;
    const trimInput = document.getElementById('carproofcartrim') as HTMLInputElement;
    const vinInput = document.querySelector('input[name="vin"]') as HTMLInputElement;
    
    const brand = brandInput?.value?.trim() || '';
    const model = modelInput?.value?.trim() || '';
    const year = yearInput?.value?.trim() || '';
    const trim = trimInput?.value?.trim() || '';
    const vin = vinInput?.value?.trim() || '';
    
    if (brand || model || year || trim || vin) {
      vehicleData = {
        vehicle: {
          make: brand,
          model: model,
          year: year,
          version: model ? '' : trim, // Clear trim if model is present to avoid mixing values
          vin: vin,
        }
      };
      
      logger.log('✅ Vehicle data found in HTML inputs:', vehicleData);
      storeInterestData(vehicleData);

      // DISABLED: Check if we're on Vehicle Details page - VDP context completely disabled
      if (siteSection === 'Vehicle Details') {
        shouldSendVDPContext = false;  // DISABLED: VDP context completely disabled to prevent any automatic messages
        
        // Build vehicle specifications from span elements
        const vehicleSpecs: string[] = [];
        
        try {
          const specsVin = document.getElementById('specsvin') as HTMLSpanElement;
          const specsKM = document.getElementById('specsKM') as HTMLSpanElement;
          const specsDoor = document.getElementById('specsDoor') as HTMLSpanElement;
          const specsSeat = document.getElementById('specsSeat') as HTMLSpanElement;
          const specsExtColor = document.getElementById('specsExtColor') as HTMLSpanElement;
          const specsIntColor = document.getElementById('specsIntColor') as HTMLSpanElement;
          const specsBodyType = document.getElementById('specsBodyType') as HTMLSpanElement;
          const specsVersion = document.getElementById('specsVersion') as HTMLSpanElement;
          const specsModel = document.getElementById('specsModel') as HTMLSpanElement;
          const specsDriveTrain = document.getElementById('specsDriveTrain') as HTMLSpanElement;
          const specsTransmission = document.getElementById('specsTransmission') as HTMLSpanElement;
          const specsFuel = document.getElementById('specsFuel') as HTMLSpanElement;
          const specsEngine = document.getElementById('specsEngine') as HTMLSpanElement;
          const specsCylinders = document.getElementById('specsCylinders') as HTMLSpanElement;
          
          // Add specifications that have values
          if (specsVin?.textContent?.trim()) vehicleSpecs.push(`VIN: ${specsVin.textContent.trim()}`);
          if (specsKM?.textContent?.trim()) vehicleSpecs.push(`Odometer: ${specsKM.textContent.trim()}`);
          if (specsDoor?.textContent?.trim()) vehicleSpecs.push(`Doors: ${specsDoor.textContent.trim()}`);
          if (specsSeat?.textContent?.trim()) vehicleSpecs.push(`Seats: ${specsSeat.textContent.trim()}`);
          if (specsExtColor?.textContent?.trim()) vehicleSpecs.push(`Exterior Color: ${specsExtColor.textContent.trim()}`);
          if (specsIntColor?.textContent?.trim()) vehicleSpecs.push(`Interior Color: ${specsIntColor.textContent.trim()}`);
          if (specsBodyType?.textContent?.trim()) vehicleSpecs.push(`Body Type: ${specsBodyType.textContent.trim()}`);
          if (specsVersion?.textContent?.trim()) vehicleSpecs.push(`Trim: ${specsVersion.textContent.trim()}`);
          if (specsModel?.textContent?.trim()) vehicleSpecs.push(`Model: ${specsModel.textContent.trim()}`);
          if (specsDriveTrain?.textContent?.trim()) vehicleSpecs.push(`Drive Train: ${specsDriveTrain.textContent.trim()}`);
          if (specsTransmission?.textContent?.trim()) vehicleSpecs.push(`Transmission: ${specsTransmission.textContent.trim()}`);
          if (specsFuel?.textContent?.trim()) vehicleSpecs.push(`Fuel Type: ${specsFuel.textContent.trim()}`);
          if (specsEngine?.textContent?.trim()) vehicleSpecs.push(`Engine: ${specsEngine.textContent.trim()}`);
          if (specsCylinders?.textContent?.trim()) vehicleSpecs.push(`Cylinders: ${specsCylinders.textContent.trim()}`);
        } catch (specsError) {
          logger.log('⚠️ Error reading vehicle specifications from spans:', specsError);
        }
        
        // Build the VDP context message with specifications
        // COMMENTED OUT TO PREVENT AUTOMATIC VDP CONTEXT MESSAGES
        // let vdpBaseMessage = `For context, the user is currently viewing the vehicle details page (VDP) for a ${year} ${brand} ${model}${trim ? ' ' + trim : ''} for VIN: ${vin}.`;
        // 
        // if (vehicleSpecs.length > 0) {
        //   vdpBaseMessage += ` Additional vehicle specifications: ${vehicleSpecs.join(', ')}.`;
        // }
        // 
        // vdpBaseMessage += ` If the user is vague about the vehicle in his message, you can assume he's referring to the vehicle he's currently viewing moving forward.`;
        // 
        // vdpContextMessage = vdpBaseMessage;
        // logger.log('✅ VDP context message prepared with specifications:', vdpContextMessage);
        
        // Temporarily disable VDP context to prevent automatic API calls
        vdpContextMessage = null;
      }
    } else {
      logger.log('⚠️ No vehicle data found in HTML inputs');
    }
  } catch (error) {
    console.error('Error reading vehicle data from HTML:', error);
  }

  // Return fresh context (never cached to ensure always current)
  const freshContext: HTMLContextData = {
    initialLanguage,
    dealerData,
    priceDisclaimer,
    vehicleData,
    shouldSendVDPContext,
    vdpContextMessage
  };

  logger.log('✅ Fresh context read complete - VDP data reflects current page only');
  return freshContext;
};

/**
 * Format dealer hours from JSON to plain English
 */
const formatDealerHours = (hoursData: any): string => {
  if (!hoursData) return 'null';
  
  const sections: string[] = [];
  
  // Process each department (Sales, Service, Parts)
  for (const [key, hours] of Object.entries(hoursData)) {
    // Extract department name (e.g., "Hours: Sales" -> "Sales Department")
    const departmentName = key.replace('Hours: ', '') + ' Department';
    const lines = [departmentName];
    
    // Process each day
    const hoursObj = hours as Record<string, string>;
    for (const [day, time] of Object.entries(hoursObj)) {
      if (time.toLowerCase() === 'closed') {
        lines.push(`${day}: Closed`);
      } else {
        // Convert "9:00am - 8:00pm" to "open from 9:00am to 8:00pm"
        const timeRange = time.replace(' - ', ' to ');
        lines.push(`${day}: open from ${timeRange}`);
      }
    }
    
    sections.push(lines.join('\n'));
  }
  
  return sections.join('\n\n');
};

export const fetchDealerInfo = async () => {
  try {
    // Check if already fetched
    const existingAddress = localStorage.getItem('Dealer_Address');
    const existingPhones = localStorage.getItem('Dealer_Phones');
    const existingHours = localStorage.getItem('Dealer_Hours');
    if (existingAddress && existingPhones && existingHours) {
      logger.log('✅ Dealer info already exists in localStorage');
      return {
        address: JSON.parse(existingAddress),
        phones: JSON.parse(existingPhones),
        hours: existingHours
      };
    }

    // Fetch dealer info from current domain
    const dealerInfoUrl = `${window.location.origin}/js/json/chatboost/dealer/dealer-info.json`;
    logger.log('📍 Fetching dealer info from:', dealerInfoUrl);
    
    const response = await fetch(dealerInfoUrl);
    if (!response.ok) {
      return null;
    }

    const dealerInfo = await response.json();
    
    // Store address
    if (dealerInfo?.address) {
      const { street, city, province, postal_code } = dealerInfo.address;
      const addressData = { street, city, province, postal_code };
      localStorage.setItem('Dealer_Address', JSON.stringify(addressData));
      logger.log('✅ Dealer address stored in localStorage:', addressData);
    }
    
    // Store phone numbers
    if (dealerInfo?.phones) {
      const { sales, service, parts } = dealerInfo.phones;
      const phonesData = { sales: sales || '', service: service || '', parts: parts || '' };
      localStorage.setItem('Dealer_Phones', JSON.stringify(phonesData));
      logger.log('✅ Dealer phones stored in localStorage:', phonesData);
    }
    
    // Store hours (formatted as plain English)
    if (dealerInfo?.hours) {
      const formattedHours = formatDealerHours(dealerInfo.hours);
      localStorage.setItem('Dealer_Hours', formattedHours);
      logger.log('✅ Dealer hours stored in localStorage');
    }
    
    return dealerInfo;
  } catch (error) {
    console.error('❌ Error fetching dealer info:', error);
  }
  return null;
};

export const initializeDealerDataAndUTM = async () => {
  // Prevent multiple initializations
  if ((window as any)._dealerDataInitialized) {
    logger.log('🚫 Dealer data already initialized, skipping');
    return;
  }
  
  // Fetch and store dealer info (address and phones)
  await fetchDealerInfo();
  
  // Start loading inventory and showroom in parallel (non-blocking)
  // This returns immediately - data will be ready when AI response arrives
  const { startParallelDataLoading } = await import('./parallelDataLoader');
  startParallelDataLoading();
  
  // Read dealer data from cookie and store in localStorage
  const dealerData = readDealerCookie();
  if (dealerData) {
    storeDealerData(dealerData);
    // Dealer data initialized from cookie
    
    // Inject UTM tracker script (only once)
    try {
      const dealerID = await getDealerID();
      if (dealerID && typeof dealerID === 'number') {
        const dealerIDStr = dealerID.toString();
        // SECURITY: Validate dealer ID to prevent XSS
        if (!/^[0-9]+$/.test(dealerIDStr) || dealerIDStr.length > 10) {
          console.error('❌ Invalid dealer ID format, skipping UTM tracker script injection');
          return dealerData;
        }
        
        // Check if UTM tracker script is already injected
        const existingScript = document.querySelector(`script[src*="utmtracker.js"][src*="did=${dealerIDStr}"]`);
        if (existingScript) {
          logger.log('🚫 UTM tracker script already exists, skipping injection');
        } else {
          const script = document.createElement('script');
          script.src = `https://stats.d2cmedia.ca/utmtracker.js?did=${encodeURIComponent(dealerIDStr)}`;
          script.async = true;
          document.head.appendChild(script);
          logger.log('✅ UTM tracker script injected with dealer ID:', dealerIDStr);
        }
      }
    } catch (error) {
      console.error('❌ Failed to get dealer ID for UTM tracking:', error);
    }
  } else {
    // No dealer data found in d2cchatbot cookie
  }
  
  // Add navigation history entry for current page
  addNavigationHistoryEntry();
  
  // Initialize page context tracking
  import('../utils/pageContextManager').then(({ PageContextManager }) => {
    PageContextManager.updatePageContext();
  });
  
  // Mark as initialized
  (window as any)._dealerDataInitialized = true;
  
  return dealerData;
};
